/**
 * A figure at full window size.
 *
 * A vector field at forty arrows across, or a heatmap, carries far more than a
 * text column's width can show. This is a plain fixed overlay rather than a
 * dialog component: the figure inside is fully interactive — sliders, crosshair
 * — and a modal that traps and re-parents its children would break the pointer
 * maths the crosshair depends on.
 */

import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "../components/ui/button.tsx";

interface FigureOverlayProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: string | undefined;
  readonly children: ReactNode;
}

export function FigureOverlay({
  open,
  onClose,
  title,
  children,
}: FigureOverlayProps) {
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    // The page behind must not scroll while the overlay is up.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ? `Figure: ${title}` : "Figure"}
      onClick={onClose}
      className="no-print fixed inset-0 z-50 flex items-center justify-center bg-background/95 p-4 sm:p-8"
    >
      <div
        // Clicks inside are for the figure, not for dismissing it.
        onClick={(event) => event.stopPropagation()}
        className="max-h-full w-full max-w-5xl overflow-y-auto"
      >
        <div className="mb-2 flex justify-end">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close figure"
          >
            <X />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}
